import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Languages, Building2, MonitorSmartphone } from "lucide-react";
import { BrandLogo } from "@/components/shell/Brand";
import { webAppHref, webAppIsExternal, CORP_URL } from "@/lib/links";
import { useI18n } from "@/i18n";

export function PublicNav() {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/product", label: lang === "tr" ? "Ürün" : "Product" },
    { to: "/#live", label: lang === "tr" ? "Canlı Zekâ" : "LIVE Intelligence" },
    { to: "/#prematch", label: lang === "tr" ? "Prematch" : "Prematch" },
    { to: "/atlas", label: "ATLAS Assistant" },
    { to: "/#how", label: lang === "tr" ? "Nasıl Çalışır" : "How it works" },
    { to: "/pricing", label: lang === "tr" ? "Fiyatlandırma" : "Pricing" },
    { to: "/responsible-use", label: lang === "tr" ? "Sorumlu Kullanım" : "Responsible Use" },
    { to: "/contact", label: lang === "tr" ? "İletişim" : "Contact" },
  ];

  const WebAppBtn = ({ mobile }: { mobile?: boolean }) => {
    const cls = mobile
      ? "flex items-center justify-center gap-1.5 rounded-lg bg-atlas-500 px-3 py-2.5 text-sm font-bold text-atlas-ink"
      : "hidden items-center gap-1.5 rounded-full bg-atlas-500 px-4 py-2 text-sm font-bold text-atlas-ink transition hover:shadow-glow sm:inline-flex";
    const label = <><MonitorSmartphone className="h-3.5 w-3.5" /> {lang === "tr" ? "Web App" : "Web App"}</>;
    return webAppIsExternal
      ? <a href={webAppHref} data-testid="nav-webapp" className={cls}>{label}</a>
      : <Link to={webAppHref} data-testid="nav-webapp" className={cls}>{label}</Link>;
  };

  return (
    <header className="sticky top-0 z-50 border-b border-line/60 bg-navy-950/70 backdrop-blur-xl" data-testid="public-nav">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 lg:px-8">
        <BrandLogo />
        <nav className="hidden items-center gap-0.5 lg:flex">
          {links.map((l) => (
            <a key={l.to} href={l.to} className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition hover:text-foreground">{l.label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={() => setLang(lang === "tr" ? "en" : "tr")} data-testid="public-lang-toggle"
            className="hidden items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-2 text-xs font-semibold text-muted-foreground transition hover:text-foreground sm:flex">
            <Languages className="h-3.5 w-3.5" /> {lang.toUpperCase()}
          </button>
          <a href={CORP_URL} data-testid="nav-corp" className="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:text-foreground lg:inline-flex">
            <Building2 className="h-3.5 w-3.5" /> SDIP Technologies
          </a>
          <WebAppBtn />
          <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" className="grid h-10 w-10 place-items-center rounded-lg border border-white/10 text-foreground lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-line bg-navy-900/95 px-4 py-4 lg:hidden" data-testid="mobile-menu">
          <nav className="grid gap-1">
            {links.map((l) => (
              <a key={l.to} href={l.to} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground">{l.label}</a>
            ))}
          </nav>
          <div className="mt-3 grid gap-2">
            <WebAppBtn mobile />
            <div className="grid grid-cols-2 gap-2">
              <a href={CORP_URL} onClick={() => setOpen(false)} className="rounded-lg border border-white/10 px-3 py-2.5 text-center text-sm font-semibold text-foreground">SDIP Technologies</a>
              <button onClick={() => { setLang(lang === "tr" ? "en" : "tr"); }} className="rounded-lg border border-white/10 px-3 py-2.5 text-sm font-semibold text-foreground">{lang === "tr" ? "EN" : "TR"}</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { lang } = useI18n();
  const cols = [
    { h: lang === "tr" ? "Ürün" : "Product", links: [[lang === "tr" ? "Canlı Zekâ" : "LIVE Intelligence", "/#live"], [lang === "tr" ? "Prematch" : "Prematch", "/#prematch"], ["ATLAS Assistant", "/atlas"], [lang === "tr" ? "Fiyatlandırma" : "Pricing", "/pricing"], [lang === "tr" ? "İletişim" : "Contact", "/contact"]] as [string, string][] },
    { h: lang === "tr" ? "Yasal" : "Legal", links: [[lang === "tr" ? "Gizlilik" : "Privacy", "/privacy"], [lang === "tr" ? "Şartlar" : "Terms", "/terms"], [lang === "tr" ? "İade" : "Refunds", "/refunds"], [lang === "tr" ? "İptal" : "Cancellation", "/cancellation"], [lang === "tr" ? "Sorumlu Kullanım" : "Responsible Use", "/responsible-use"]] as [string, string][] },
  ];
  const domains: [string, string][] = [
    ["SDIP Technologies", CORP_URL],
    [lang === "tr" ? "Web Uygulaması" : "Web App", webAppHref],
  ];
  return (
    <footer className="border-t border-line bg-navy-950/60" data-testid="public-footer">
      <div className="mx-auto max-w-7xl px-4 py-14 lg:px-8">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <BrandLogo />
            <p className="mt-4 max-w-xs text-sm text-muted-foreground">
              {lang === "tr" ? "SDIP Technologies Ltd — premium yapay zekâ destekli futbol zekâsı ve canlı maç analitiği." : "SDIP Technologies Ltd — premium AI-powered football intelligence and live match analytics."}
            </p>
            <p className="mt-4 text-[11px] text-muted-foreground">
              {lang === "tr" ? "SDIP AI bir bahis, kumar veya bahisçi platformu değildir. Yalnızca spor zekâsı ve analitik sunar." : "SDIP AI is not a betting, gambling or bookmaker platform. It provides sports intelligence and analytics only."}
            </p>
            <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
              SDIP TECHNOLOGIES LTD · Company No. 17478518<br />Registered in England and Wales
            </p>
          </div>
          {cols.map((c) => (
            <div key={c.h}>
              <p className="eyebrow mb-4">{c.h}</p>
              <ul className="space-y-2.5">
                {c.links.map(([label, to]) => (
                  <li key={to}><a href={to} className="text-sm text-muted-foreground transition hover:text-atlas-300">{label}</a></li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <p className="eyebrow mb-4">{lang === "tr" ? "Ekosistem" : "Ecosystem"}</p>
            <ul className="space-y-2.5">
              {domains.map(([label, href]) => (
                <li key={label}><a href={href} className="text-sm text-muted-foreground transition hover:text-atlas-300">{label}</a></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-line pt-6 text-xs text-muted-foreground sm:flex-row">
          <span>© {new Date().getFullYear()} SDIP Technologies. All rights reserved.</span>
          <span>ATLAS Intelligence Engine</span>
        </div>
      </div>
    </footer>
  );
}
